import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useSEO } from '../hooks/useSEO';

export default function Signup() {
  useSEO(
    "Get Started with Paygent | Merchant Onboarding Request",
    "Submit a Paygent onboarding request for merchant payment services and Tapn tap-to-pay activation. Secure merchant completion is handled through Paygent and NMI."
  );

  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: '', first_name: '', last_name: '', contact_email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_email) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/merchant/onboard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        navigate('/success');
      } else {
        console.error('[Signup API Error]:', data);
        setError(data.error || 'Failed to create merchant account. Please check your details and try again.');
      }
    } catch (err: any) {
      console.error('[Signup Network Error]:', err);
      setError('A network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title="Create Your Account"
      subtitle="Join Paygent and get access to the TapN Soft POS and industry-leading payment gateways."
      badge="MERCHANT SIGNUP"
    >
      <div className="max-w-3xl mx-auto">
        <div className="bg-white p-8 md:p-10 rounded-3xl border border-gray-100 shadow-xl shadow-blue-900/5">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-start gap-3">
              <i className="ri-error-warning-line text-xl mt-0.5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Legal Business Name *</label>
              <input 
                type="text" 
                required 
                value={form.business_name} 
                onChange={e => setForm({...form, business_name: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-base bg-white" 
                placeholder="Acme Corp LLC" 
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">First Name *</label>
                <input 
                  type="text" 
                  required 
                  value={form.first_name} 
                  onChange={e => setForm({...form, first_name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-base bg-white" 
                  placeholder="Jane" 
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Last Name *</label>
                <input 
                  type="text" 
                  required 
                  value={form.last_name} 
                  onChange={e => setForm({...form, last_name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-base bg-white" 
                  placeholder="Doe" 
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Business Email *</label>
                <input 
                  type="email" 
                  required 
                  value={form.contact_email} 
                  onChange={e => setForm({...form, contact_email: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-base bg-white" 
                  placeholder="founders@company.com" 
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number</label>
                <input 
                  type="tel" 
                  value={form.phone} 
                  onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-base bg-white" 
                  placeholder="(555) 123-4567" 
                />
              </div>
            </div>
            
            <div className="pt-4">
              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2"
                style={{ boxShadow: '0 4px 16px rgba(37,99,235,0.25)' }}>
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Processing Application...
                  </>
                ) : (
                  'Get Started'
                )}
              </button>
              <p className="text-center text-xs text-gray-500 mt-4">
                By signing up, you agree to Paygent's Terms of Service and Privacy Policy.
              </p>
            </div>
          </form>
        </div>
      </div>
    </PageLayout>
  );
}
